import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

function authHeaders() {
  const token = localStorage.getItem('accessToken');
  return { Authorization: `Bearer ${token}` };
}

// Dashboard stats (/api/analytics/dashboard)
export interface DashboardStats {
  patrons: { total: number; active: number; churnedOrPaused: number };
  revenue: { total: number; recentPayments: number };
  content: { totalPosts: number; recentPosts: number };
}

// Geographic summary (/api/analytics/geographic/summary)
export interface GeographicSummary {
  totalActivePatrons: number;
  countryCount: number;
  regionCount: number;
  cityCount: number;
  topCountries: { country: string; count: number; percentage: number }[];
  timezoneDistribution: { timezone: string; count: number; percentage: number }[];
  diversityIndex: number;
}

// Engagement summary (/api/analytics/engagement/summary)
export interface EngagementSummaryData {
  overallScore: number;
  averagePerPost: number;
  totalInteractions: {
    views: number; likes: number; comments: number; shares: number; bookmarks: number; reactions: number; downloads: number; revenue: number;
  };
  topPosts: any[]; // Keeping flexible; can refine later
  trend: { date: string; score: number; posts: number }[];
  breakdownPercentages: { views: number; likes: number; comments: number; shares: number; bookmarks: number; reactions: number; downloads: number };
}

// Revenue forecast summary (/api/analytics/revenue-forecast/summary)
export interface RevenueForecastSummary {
  trend: { date: string; value?: number; predicted?: number }[] | any[];
  currentMonthlyRevenue: number;
  predictedNextMonth: number;
  predictedNext3Months: number;
  confidenceLevel: number; // percent 0-100
  accuracy: { mape: number; rmse: number; r2: number };
  seasonality: { detected: boolean; period: number | null };
}

export const adminPlatformAnalyticsService = {
  async getDashboardStats(): Promise<DashboardStats> {
    const res = await axios.get(`${API_BASE_URL}/api/analytics/dashboard`, { headers: authHeaders() });
    return res.data as DashboardStats;
  },
  async getGeographicSummary(): Promise<GeographicSummary> {
    const res = await axios.get(`${API_BASE_URL}/api/analytics/geographic/summary`, { headers: authHeaders() });
    if (!res.data?.success) throw new Error(res.data?.message || 'Failed to load geographic summary');
    return res.data.data as GeographicSummary;
  },
  async getEngagementSummary(): Promise<EngagementSummaryData> {
    const res = await axios.get(`${API_BASE_URL}/api/analytics/engagement/summary`, { headers: authHeaders() });
    if (!res.data?.success) throw new Error(res.data?.message || 'Failed to load engagement summary');
    return res.data.data as EngagementSummaryData;
  },
  async getRevenueForecastSummary(): Promise<RevenueForecastSummary> {
    const res = await axios.get(`${API_BASE_URL}/api/analytics/revenue-forecast/summary`, { headers: authHeaders() });
    if (!res.data?.success) throw new Error(res.data?.error || 'Failed to load forecast summary');
    return res.data.data as RevenueForecastSummary;
  }
};

export default adminPlatformAnalyticsService;
